import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBuilderOverlay } from '@printtat/builder';
import { ArrowLeft, Store, ShoppingCart, Pencil, Package } from 'lucide-react';
import { catalogApi } from '@/services/catalogApi';
import { queryKeys } from '@/lib/queryKeys';
import { formatPrice } from '@printtat/shared';
import {
  PageContent,
  PageHeader,
  Button,
  IconButton,
  LoadingState,
} from '@printtat/ui';
import { useCartStore } from '@/stores/cartStore';
import { cn } from '@/lib/utils';

export const CatalogProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const addCatalogItem = useCartStore((s) => s.addCatalogItem);

  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.catalog.detail(slug!),
    queryFn: () => catalogApi.detail(slug!),
    enabled: !!slug,
  });

  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [forking, setForking] = useState(false);

  const variants = useMemo(() => (product?.variants ?? []).filter((v) => v.isActive && v.stockCount > 0), [product?.variants]);

  const colors = useMemo(() => {
    const seen = new Map<string, { name: string; hex: string }>();
    for (const v of variants) {
      if (!seen.has(v.color)) seen.set(v.color, { name: v.color, hex: v.colorHex });
    }
    return [...seen.values()];
  }, [variants]);

  const sizes = useMemo(() => {
    const seen = new Set<string>();
    return variants
      .filter((v) => !selectedColor || v.color === selectedColor)
      .filter((v) => {
        if (seen.has(v.size)) return false;
        seen.add(v.size);
        return true;
      })
      .map((v) => v.size);
  }, [variants, selectedColor]);

  const selectedVariant = useMemo(() => {
    if (!selectedColor || !selectedSize) return null;
    return variants.find((v) => v.color === selectedColor && v.size === selectedSize) ?? null;
  }, [variants, selectedColor, selectedSize]);

  const effectivePrice = product
    ? product.price + (selectedVariant?.additionalPrice ?? 0)
    : 0;

  // Auto-select first color
  if (colors.length > 0 && !selectedColor) {
    setSelectedColor(colors[0].name);
  }

  const handleAddToCart = async () => {
    if (!selectedVariant || !product) return;
    setAdding(true);
    try {
      await addCatalogItem(product.id, selectedVariant.id, quantity);
    } catch {
      // handled by store
    } finally {
      setAdding(false);
    }
  };

  const openBuilder = useBuilderOverlay((s) => s.openBuilder);

  const handleCustomize = async () => {
    if (!product) return;
    setForking(true);
    try {
      const { designId } = await catalogApi.fork(product.id);
      openBuilder({ designId, productSlug: product.productSlug });
    } catch {
      // fork failed
    } finally {
      setForking(false);
    }
  };

  if (isLoading) {
    return (
      <PageContent className="overflow-y-auto h-screen">
        <LoadingState variant="skeleton" columns={1} rows={6} />
      </PageContent>
    );
  }

  if (!product) {
    return (
      <PageContent className="overflow-y-auto h-screen">
        <div className="text-center py-20">
          <p className="text-gray-500">Product not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/catalog')}>
            Back to catalog
          </Button>
        </div>
      </PageContent>
    );
  }

  const images = [product.previewUrl, ...product.galleryUrls].filter(Boolean) as string[];

  return (
    <PageContent className="overflow-y-auto h-screen">
      <div className="flex items-center gap-3 mb-6">
        <IconButton size="sm" variant="outline" onClick={() => navigate('/catalog')}>
          <ArrowLeft size={16} />
        </IconButton>
        <PageHeader icon={Store} title={product.displayName} description={product.productName} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        {/* Left — Images */}
        <div className="space-y-3">
          <div className="aspect-square rounded-xl overflow-hidden bg-gray-50 border">
            {images[0] ? (
              <img src={images[0]} alt={product.displayName} crossOrigin="anonymous" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package size={48} className="text-gray-300" />
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2">
              {images.slice(1).map((url, i) => (
                <div key={i} className="w-20 h-20 rounded-lg overflow-hidden bg-gray-50 border">
                  <img src={url} alt="" crossOrigin="anonymous" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — Details + Picker */}
        <div className="space-y-6">
          <div>
            <p className="text-2xl font-bold text-gray-900">{formatPrice(effectivePrice)}</p>
            {selectedVariant && selectedVariant.additionalPrice > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Base {formatPrice(product.price)} + variant {formatPrice(selectedVariant.additionalPrice)}
              </p>
            )}
          </div>

          {product.description && (
            <p className="text-sm text-gray-600">{product.description}</p>
          )}

          {/* Color picker */}
          {colors.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Color: <span className="text-gray-900">{selectedColor}</span>
              </p>
              <div className="flex gap-2">
                {colors.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => {
                      setSelectedColor(c.name);
                      setSelectedSize(null);
                    }}
                    className={cn(
                      'w-8 h-8 rounded-full border-2 transition-all',
                      selectedColor === c.name ? 'border-gray-900 scale-110' : 'border-gray-200 hover:border-gray-400',
                    )}
                    style={{ backgroundColor: c.hex }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Size picker */}
          {sizes.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Size</p>
              <div className="flex flex-wrap gap-2">
                {sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={cn(
                      'px-4 py-2 text-sm rounded-lg border transition-colors',
                      selectedSize === size
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 hover:border-gray-400 text-gray-700',
                    )}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Quantity</p>
            <div className="flex items-center gap-3">
              <IconButton
                size="sm"
                variant="outline"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
              >
                -
              </IconButton>
              <span className="text-sm font-semibold w-8 text-center">{quantity}</span>
              <IconButton
                size="sm"
                variant="outline"
                onClick={() => setQuantity((q) => q + 1)}
                disabled={selectedVariant ? quantity >= selectedVariant.stockCount : false}
              >
                +
              </IconButton>
              {selectedVariant && selectedVariant.stockCount <= 5 && (
                <span className="text-xs text-amber-500">{selectedVariant.stockCount} left</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleAddToCart}
              loading={adding}
              disabled={!selectedVariant}
              className="flex-1"
            >
              <ShoppingCart size={14} />
              Add to Cart
            </Button>
            <Button
              variant="outline"
              onClick={handleCustomize}
              loading={forking}
            >
              <Pencil size={14} />
              Customize
            </Button>
          </div>
        </div>
      </div>
    </PageContent>
  );
};
